<template>
  <div class="manageFinancePrograms">
    <div class="mb-3">
      <div
        class="manageFinancePrograms__logo d-flex flex-wrap align-items-center justify-between sm:flex-nowrap sm:justify-normal"
      >
        <img data-cy="lenderLogo" class="shrink-0 mr-4" :src="lenderLogo" />
        <h3 class="sm:flex-1">Manage Financing Programs</h3>
        <Button
          text="Refresh Programs"
          icon="far fa-rotate"
          class="grow ml-auto mt-4 sm:grow-0 sm:mt-0"
          :saving="refreshing"
          :disabled="refreshing"
          @click="refreshPrograms"
        />
      </div>
    </div>
    <div v-if="activeDepartmentsLoaded" class="departments">
      <div class="departments__header">
        <div class="departments__title">
          Departments with Financing Available
        </div>
        <i
          class="departments__edit-icon far fa-pen-to-square link"
          @click="openDepartmentModal"
        ></i>
      </div>
      <div class="departments__list">
        <span v-for="(department, i) in activeDepartments" :key="department.id">
          <span :class="{ 'strike-through': department.archived }">
            {{ department.label }}
          </span>
          <span v-if="i !== activeDepartments.length - 1"> | </span>
        </span>
      </div>
    </div>
    <LoanProgramsDatatable
      data-cy="loanProgramsDatatable"
      :filters="filters"
      :lender="lender"
      class="loanProgramsDatatable mb-3"
    />
    <DepartmentModal
      v-if="departmentModalOpen"
      :department-options="departmentOptions"
      :active-department-ids="activeDepartmentIds"
      @hide-modal="closeDepartmentModal"
      @submit="handleDepartmentUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";

import Button from "@/components/Button.vue";
import DepartmentModal from "./DepartmentModal.vue";
import LoanProgramsDatatable from "./LoanProgramsDatatable.vue";

const store = useStore();

const props = defineProps({
  filters: {
    type: Array,
    default: () => []
  },
  departmentOptions: {
    type: Array,
    required: true,
    default: () => []
  },
  lender: {
    type: String,
    default: ""
  },
  lenderLogo: {
    type: String,
    default: ""
  }
});

const activeDepartmentIds = ref([]);
const activeDepartmentsLoaded = ref(false);
const departmentModalOpen = ref(false);
const defaultDepartment = {
  id: 0,
  label: "All (Default)",
  value: 0,
  archived: false
};
const refreshing = ref(false);

const activeDepartments = computed(() => {
  const departments = props.departmentOptions.filter((option) => {
    return activeDepartmentIds.value.includes(option.value);
  });
  if (departments.length === 0) {
    departments.push(defaultDepartment);
  }
  return departments;
});

onMounted(() => loadLenderSettings());

async function loadLenderSettings() {
  const settings = await store.dispatch("fetchLenderSettings", {
    lender: props.lender
  });
  updateActiveDepartmentIds(settings?.department_ids);
  activeDepartmentsLoaded.value = true;
}

async function refreshPrograms() {
  refreshing.value = true;
  try {
    await store.dispatch("refreshLoanPrograms", props.lender);
    window.notify.success("Programs refreshed successfully!");
    EventBus.$emit("refresh-datatable");
  } catch (error) {
    window.notify.error(error);
    Sentry.captureException(error);
    console.error("Error refreshing programs", error);
  } finally {
    refreshing.value = false;
  }
}

function openDepartmentModal() {
  departmentModalOpen.value = true;
}

function closeDepartmentModal() {
  departmentModalOpen.value = false;
}

async function handleDepartmentUpdate(departmentIds) {
  try {
    const settings = await store.dispatch("updateLenderSettings", {
      lender: props.lender,
      department_ids: departmentIds
    });
    updateActiveDepartmentIds(settings?.department_ids);
    window.notify.success("Departments updated successfully!");
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error updating lender settings", error);
  } finally {
    closeDepartmentModal();
  }
}

function updateActiveDepartmentIds(departmentIds) {
  activeDepartmentIds.value = (departmentIds || []).map(String);
}
</script>

<style lang="scss" scoped>
.manageFinancePrograms {
  &__logo {
    img {
      width: 110px;
    }
    h3 {
      max-width: calc(100% - 126px);
      text-align: right;

      @media (min-width: theme("screens.sm")) {
        max-width: none;
        text-align: start;
      }
    }
  }
}
.departments {
  display: inline-flex;
  flex-direction: column;
  margin: 8px 0px 24px 0px;
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 16px;

  &__header {
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-bottom: 8px;
  }
  &__title {
    color: var(--font-color);
    font-weight: 700;
    line-height: 16px;
  }
  &__edit-icon {
    margin-left: 4px;
  }
}
</style>
